import { useCallback, useContext } from "react";
import { v4 as uuid } from "uuid";

import { InstoreHttpHeaderNames, LocalStorageKeys } from "@is/constants";
import { ViewStateContext } from "@is/contexts";
import { initialContentViewState } from "@is/contexts/ViewStateContext/ContentViewState";
import { useStoreEvents } from "@is/hooks/useStoreEvents";
import { LocalStorageService } from "@is/services";
import { TrackingEventTypes, trackEvent } from "@is/services/EventTracking";
import { api } from "@is/services/api";
import { ScannerDetails } from "@is/types/scanner";
import {
  clearFinishedSession,
  clearQRCheckoutSession,
  isFinishedSession,
} from "@is/utils/session";

export const useSession = () => {
  const { updateViewState } = useContext(ViewStateContext).content;
  const { storeEvent } = useStoreEvents();

  const previousSessionId = LocalStorageService.getItem(
    LocalStorageKeys.SessionId
  );

  const scannerDetails: ScannerDetails = JSON.parse(
    LocalStorageService.getItem(LocalStorageKeys.ScannerDetails)
  );

  const createSession = useCallback(() => {
    const newSessionId = uuid();
    LocalStorageService.setItem(LocalStorageKeys.SessionId, newSessionId);
    LocalStorageService.removeItem(LocalStorageKeys.SearchId);
    LocalStorageService.removeItem(LocalStorageKeys.CountryCode);
    LocalStorageService.removeItem(LocalStorageKeys.CheckoutInfo);
    LocalStorageService.removeItem(LocalStorageKeys.PhoneCountryCode);
    // The record only ever describes a session that is now gone — cleared
    // AFTER the new id is stored, so an interruption can never leave the
    // checked-out id behind without its record.
    clearFinishedSession();
    clearQRCheckoutSession();

    api.setHeader(InstoreHttpHeaderNames.SessionId, newSessionId);

    storeEvent({
      eventType: TrackingEventTypes.ResetSessionManually,
      data: { sessionId: previousSessionId },
      createdAt: new Date().toISOString(),
      sessionId: previousSessionId,
    });

    if (!!scannerDetails && Object.keys(scannerDetails)?.length) {
      trackEvent({
        eventType: TrackingEventTypes.ConnectedScannerDetected,
        data: {},
      });
    }
  }, [previousSessionId, scannerDetails, storeEvent]);

  const resetSession = useCallback(() => {
    createSession();
    updateViewState(initialContentViewState);
  }, [createSession, updateViewState]);

  return {
    isSessionExist: !!LocalStorageService.getItem(LocalStorageKeys.SessionId),
    /** The stored session already completed checkout, so its basket is locked
     *  backend-side and it must not be picked up again. */
    isSessionFinished: isFinishedSession(previousSessionId),
    createSession,
    resetSession,
  };
};
